import { useEffect } from 'react';

interface SEOMetaProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  jsonLd?: object | object[];
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

export default function SEOMeta({
  title,
  description,
  keywords,
  canonicalPath = '/',
  ogImage,
  ogType = 'website',
  jsonLd,
}: SEOMetaProps) {
  const canonical = `${SITE_URL}${canonicalPath}`;
  const serialized = jsonLd ? JSON.stringify(jsonLd) : null;

  useEffect(() => {
    if (!serialized) return;

    const schemas: object[] = Array.isArray(jsonLd) ? (jsonLd as object[]) : [jsonLd as object];
    const inserted: HTMLScriptElement[] = schemas.map((schema) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.textContent = JSON.stringify(schema);
      el.setAttribute('data-quizvibe-jsonld', 'true');
      document.head.appendChild(el);
      return el;
    });

    return () => {
      inserted.forEach((el) => el.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized]);

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="last-modified" content={new Date().toISOString().slice(0, 10)} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      {ogImage && <meta property="og:image" content={ogImage} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
    </>
  );
}
